import { useState } from 'react';
import { Box } from '@mui/material';
import { useParams } from 'react-router-dom';
import invariant from 'tiny-invariant';
import { useStore } from '@store/GlobalStore';
import { shallow } from 'zustand/shallow';
import { Table } from '@components/table/Table';
import {
  SubjectivityItem,
  useSearchSubjectivities,
  useDeleteSubjectivity,
  useUpdateSubjectivity,
  subjectivityQueryKeys,
} from '@services/subjectivity';
import { SubjectivityStatus } from '@lio/rule-engine';
import { useQuote } from '@services/quotes';
import { quoteStatusTerminal } from '@models/quotes';
import { ConfirmationDialog, AddFilesDialog } from '@components/dialog';
import { useQueryClient } from '@tanstack/react-query';
import { documentQueryKey, useCreateDocument, useDeleteDocument } from '@services/document';
import { useCreateFile, useDeleteFile } from '@services/file';
import { uploadFile } from '@services/file';
import { PostArtifact } from '@models/note';
import { acceptedFileMimeTypes } from '@utils/fileUtils';
import { DocumentType } from '@models/document';
import { Plus } from '@assets/svgs';
import { Button } from '@mui/material';
import { writingCompanyIds } from '@models/productOffering';
import cuid from 'cuid';
import { getSubjectivitiesColumns } from './SubjectivitiesPanel/getSubjectivitiesColumns';
import { SubjectivityDialog } from '@pages/UnderwriterToolkit/tab-content/SubjectivitiesPanel/SubjectivityDialog';
import { underwritingTabStyles } from '@pages/UnderwriterToolkit/tab-content/UnderwritingTab.styles';

export enum DialogType {
  Edit = 'edit',
  Delete = 'delete',
  Upload = 'upload',
  Manual = 'manual',
  Library = 'library',
}

type TSubjectivitiesTable = {
  items: SubjectivityItem[];
  params: {
    quoteId: string;
    policyId: string;
    sort: string;
    pageOffset: number;
    pageLimit: number;
  };
};

export const SubjectivitiesTable = () => {
  const { quoteId } = useParams<'quoteId'>();
  invariant(quoteId);

  const { quote } = useQuote(quoteId);
  const { isInternalUser, isUnderwriter, isLIOContractor, isBroker } = useStore((state) => state.userAuth, shallow);
  const { deleteSubjectivity, isPending: isDeleteSubjectivityLoading } = useDeleteSubjectivity(quoteId);
  const { updateSubjectivity } = useUpdateSubjectivity(quoteId);
  const { createDocumentAsync } = useCreateDocument();
  const { createFileAsync } = useCreateFile();
  const { deleteFileAsync } = useDeleteFile();
  const { deleteDocumentAsync } = useDeleteDocument();
  const client = useQueryClient();

  const [dialogMode, setDialogMode] = useState<DialogType | null>(null);
  const [editSubjectivity, setEditSubjectivity] = useState<SubjectivityItem | null>(null);
  const [subjectivityToDelete, setSubjectivityToDelete] = useState<SubjectivityItem | null>(null);
  const [subjectivityToUpload, setSubjectivityToUpload] = useState<SubjectivityItem | null>(null);

  const isQuoteTerminal = !!quote?.status.name && quoteStatusTerminal.includes(quote?.status.name);

  const handleDeleteClick = (subjectivity: SubjectivityItem, event: React.MouseEvent) => {
    event.stopPropagation();
    setSubjectivityToDelete(subjectivity);
    setDialogMode(DialogType.Delete);
  };

  const handleUploadClick = (subjectivity: SubjectivityItem, event: React.MouseEvent) => {
    event.stopPropagation();
    setSubjectivityToUpload(subjectivity);
    setDialogMode(DialogType.Upload);
  };

  const handleConfirmDelete = async () => {
    if (subjectivityToDelete) {
      try {
        await deleteSubjectivity(subjectivityToDelete.id);
        setDialogMode(null);
        setSubjectivityToDelete(null);
      } catch (error) {
        // Error handling is done in the useDeleteSubjectivity hook
        // The dialog will stay open if deletion fails
        console.error('Failed to delete subjectivity:', error);
      }
    }
  };

  const handleUploadFiles = async (files: PostArtifact[]) => {
    if (!subjectivityToUpload) return;

    try {
      await Promise.all(
        files
          .filter((f) => acceptedFileMimeTypes.includes(f.file.type))
          .map(async (f) => {
            let createdFileId;
            let createdDocumentId;
            try {
              const documentId = cuid();
              const file = await createFileAsync({
                description: f.description,
                awsS3Id: `document/${documentId}`,
                fileName: f.name,
                originalFileName: f.name,
                fileType: f.mimetype,
              });
              if (!file.uploadUrl) {
                throw new Error('Invalid file generated');
              }
              createdFileId = file.id;
              await uploadFile(file.uploadUrl, f.file);
              const document = await createDocumentAsync({
                fileId: file.id,
                subjectivityId: subjectivityToUpload.id,
                documentTypeId: DocumentType.Other.id,
              });
              createdDocumentId = document.id;
              return document.id;
            } catch (e) {
              console.error(e);
              if (createdFileId) {
                await deleteFileAsync({ fileId: createdFileId });
              }
              if (createdDocumentId) {
                await deleteDocumentAsync({ documentId: createdDocumentId });
              }
              return undefined;
            }
          }),
      );

      if (subjectivityToUpload.documents.length === 0) {
        await updateSubjectivity({
          status: SubjectivityStatus.Closed,
          subjectivityId: subjectivityToUpload.id,
        });
      }

      client.refetchQueries({ queryKey: documentQueryKey.search });
      client.invalidateQueries({ queryKey: subjectivityQueryKeys.search(quoteId) });
    } catch (error) {
      console.error('Error uploading files:', error);
    }
  };

  const canUploadToSubjectivity = (subjectivity: SubjectivityItem) => {
    if (isQuoteTerminal || subjectivity.deletedAt) return false;
    if (subjectivity.status !== SubjectivityStatus.Active) return false;
    if (isInternalUser || isUnderwriter || isLIOContractor) return true;
    if (isBroker && subjectivity.isBrokerSubjectivity) return true;
    return false;
  };

  const handleRowClick = (row: SubjectivityItem) => {
    setEditSubjectivity(row);
    setDialogMode(DialogType.Edit);
  };

  // Add button logic
  const isSpecialtyWritingCompany = quote?.writingCompany?.id === writingCompanyIds.specialty;
  const canAddSubjectivity = isInternalUser || (isBroker && isSpecialtyWritingCompany);

  const columns = getSubjectivitiesColumns(
    canUploadToSubjectivity,
    isUnderwriter,
    isLIOContractor,
    isBroker,
    isInternalUser,
    isQuoteTerminal,
    handleUploadClick,
    handleDeleteClick,
  );

  const useSearchSubjectivitiesWrapper = (params: { pageOffset: number; pageLimit: number; sort?: string }) => {
    const result = useSearchSubjectivities({ ...params, quoteId });
    return {
      ...result,
      data: {
        items: result.subjectivities?.items || [],
        pagination: result.subjectivities?.pagination,
      },
    };
  };

  function handleClearDialogMode() {
    setDialogMode(null);
  }

  return (
    <Box sx={underwritingTabStyles.activityPanel}>
      <Box sx={underwritingTabStyles.tableWrapper}>
        <Table<TSubjectivitiesTable>
          ariaLabel='Subjectivities Table'
          columns={columns}
          onLoadData={useSearchSubjectivitiesWrapper}
          rowConfig={{
            onRowClick: ({ row }) => handleRowClick(row),
            getRowHeight: () => 50,
            getRowClassName: ({ row }) => {
              if (row.deletedAt) return 'deleted-row';
              if (isQuoteTerminal) return 'terminal-row';
              return '';
            },
          }}
          hidePagination
          sxProps={{
            ...underwritingTabStyles,
          }}
          paginationConfig={{
            currentPage: 0,
            pageOffset: 0,
            rowsPerPage: 100,
          }}
        />
      </Box>
      {canAddSubjectivity && (
        <Box sx={underwritingTabStyles.buttonContainer}>
          <Button
            variant='outlined'
            onClick={() => setDialogMode(DialogType.Manual)}
            disabled={isQuoteTerminal}
            startIcon={<Plus />}
          >
            Add Subjectivity
          </Button>
        </Box>
      )}
      <ConfirmationDialog
        isOpen={dialogMode === DialogType.Delete}
        onCancel={handleClearDialogMode}
        description={`Are you sure you wish to delete this subjectivity: "${subjectivityToDelete?.label}"?`}
        isLoading={isDeleteSubjectivityLoading}
        buttonConfig={{
          confirmText: 'Delete',
          onConfirm: handleConfirmDelete,
          secondaryText: 'Cancel',
          onSecondaryButtonClick: handleClearDialogMode,
        }}
      />

      <AddFilesDialog
        open={dialogMode === DialogType.Upload}
        handleCloseDialog={handleClearDialogMode}
        onDone={({ uploadedFiles }) => handleUploadFiles(uploadedFiles)}
      />
      <SubjectivityDialog
        open={dialogMode === DialogType.Manual || dialogMode === DialogType.Edit}
        onClose={handleClearDialogMode}
        subjectivity={dialogMode === DialogType.Edit ? editSubjectivity : undefined}
        setSubjectivity={setEditSubjectivity}
      />
    </Box>
  );
};
